<script setup>
import { SvgFacebook, SvgLinkedin24, SvgYt24, SvgIg24 } from "#components";
import { htmlPurify } from "~/lib/utils";
import gsap from "gsap";

const route = useRoute();
const router = useRouter();
const switchLocalePath = useSwitchLocalePath();
const localePath = useLocalePath();
const emitter = useEmitter();

const isDesktop = useBreakpoint();
const isTablet = useBreakpoint("tablet");

const { locale, locales } = useI18n();
const { mode, headerMode, setIsSearchOpen } = useMode();
const { openModal } = useExternalLinkModal();
const { height } = useWindowSize();

const bgRef = ref(null);
const menuOverlay = ref(null);
const responsiveMenuRef = ref(null);
const gamaGlobeRef = ref(null);
let tl = null;

const isActive = ref(false);
const isMenuTextActive = ref(false);
const isMenuOpen = ref(false);
const showHeader = ref(false);
const languageListOpen = ref(false);
const lastScrollY = ref(0);

const props = defineProps(["menuLinks", "snsData"]);

const proccessedMenuLinks = computed(() => {
    return props.menuLinks.map((item) => {
        return {
            ...item,
            name: item.name.replace(/<br\s*[^>]*>/gi, ""),
        };
    });
});

// responsive arrow icon stroke width
const strokeWidth = computed(() => {
    return isTablet.value ? 1.2 : 1.25;
});

// responsive menu text color is white when isCurrentRoute is true
const normalizePath = (p) =>
    p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;

const isCurrentRoute = (path) => {
    const currentPath = normalizePath(route.path);
    const localizedPath = normalizePath(localePath(path));
    return currentPath === localizedPath;
};

const isDark = computed(() => {
    return headerMode.value === "dark";
});

const languageListConfig = ref({
    wheels: [
        {
            data: locales.value.map((loc, index) => ({
                value: loc.code,
                label: loc.value,
                id: index,
            })),
        },
    ],
    initValue: locales.value.find((loc) => loc.code === locale.value).value,
    keyMap: {
        value: "label",
    },
});

const switchLanguageMobile = (loc) => {
    toggleMenu();
    setTimeout(() => {
        router.push(switchLocalePath(loc.value));
    }, 500);
};

const switchLanguageDesktop = (loc) => {
    setTimeout(() => {
        router.push(switchLocalePath(loc.code));
    }, 500);
};

const handleExternalLinkClick = async (link) => {
    if (link.type === "linkExternal") openModal(link.href);
};

const getSnsIcon = (name) => {
    const map = {
        facebook: SvgFacebook,
        instagram: SvgIg24,
        youtube: SvgYt24,
        linkedin: SvgLinkedin24,
    };
    return map[name];
};

const toggleMenu = async () => {
    if (tl) {
        tl.killTweensOf([gamaGlobeRef.value, menuOverlay.value,responsiveMenuRef.value]);
        tl.kill();
        tl = null;
    }

    isMenuOpen.value = !isMenuOpen.value;
    document.body.style.overflow = isMenuOpen.value ? "hidden" : "auto";

    if (isMenuOpen.value) {
        isActive.value = true;
        await nextTick();
        await new Promise((resolve) => requestAnimationFrame(resolve));

        gsap.set(responsiveMenuRef.value, { yPercent: 0 });

        tl = gsap.timeline();
        tl.fromTo(
            [gamaGlobeRef.value, menuOverlay.value, bgRef.value],
            { yPercent: -100 },
            {
                yPercent: 0,
                duration: 0.6,
                ease: "circ.inOut",
                onStart: () => {
                    isMenuTextActive.value = true;
                },
            },
        );
    } else {
        tl = gsap.timeline();
        tl.to([responsiveMenuRef.value, gamaGlobeRef.value, bgRef.value], {
            yPercent: -100,
            duration: 0.7,
            ease: "circ.inOut",
        }).fromTo(
            menuOverlay.value,
            { yPercent: 0 },
            {
                yPercent: -100,
                duration: 0.1,
                ease: "power3.out",
                onComplete: () => {
                    isActive.value = false;
                    isMenuTextActive.value = false;
                },
            },
            "-=0.3",
        );
    }
};

// close menu when search popup is closed
emitter.on("close-menu", () => {
    if (isMenuOpen.value) {
        setTimeout(() => {
            toggleMenu();
        }, 500);
    }
});

const { isPressed, pressClose } = usePressClose(toggleMenu);

const currentScrollY = ref(0);
const scrollDirection = ref("down");

const handleScroll = () => {
    currentScrollY.value = window.scrollY;

    if (currentScrollY.value > lastScrollY.value) {
        scrollDirection.value = "down";
    } else {
        scrollDirection.value = "up";
    }

    if (
        currentScrollY.value > lastScrollY.value + 10 &&
        currentScrollY.value > 0
    ) {
        showHeader.value = false;
        lastScrollY.value = currentScrollY.value;
    } else if (currentScrollY.value < lastScrollY.value - 10) {
        showHeader.value = true;
        lastScrollY.value = currentScrollY.value;
    }

    languageListOpen.value = false;
};

const handleResize = () => {
    if (!isDesktop.value) {
        isMenuOpen.value = false;
        isActive.value = false;
        isMenuTextActive.value = false;
    }
};

onMounted(() => {
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    if (!route.name.includes("gplay-id")) {
        setTimeout(() => {
            showHeader.value = true;
        }, 1700);
    }

    watchEffect(() => {
        if (route.name.includes("gplay-id")) {
            showHeader.value = false;
        } else {
            showHeader.value = true;
        }
    });
});

onUnmounted(() => {
    window.removeEventListener("scroll", handleScroll);
    window.removeEventListener("resize", handleResize);
});
</script>

<template>
    <header
        class="fixed left-0 top-0 w-full transition-[transform,shadow] before:absolute before:inset-0 before:z-[-1] before:shadow-[0px_3px_4px_-2px_rgba(0,0,0,0.15)] before:transition-opacity before:duration-100 before:ease-linear lg:w-[calc(100vw-var(--scrollbar-width))] lg:shadow-none"
        :class="{
            // egg mode has its own transition
            egg: mode === 'egg' && currentScrollY < height,
            'z-[1500] translate-y-0 duration-700 ease-in-out-quint':
                showHeader && !isMenuOpen,
            'z-[1500] translate-y-[-100%] bg-transparent duration-300 ease-in-out-sine':
                !showHeader && !isMenuOpen,
            'z-[2000] h-full pb-[170.48vw] xs:pb-[670px] sm:pb-[950px]':
                isActive,
            'overflow-hidden': !isDesktop,
            'before:bg-base-bgGrey': headerMode === 'light' && !isActive,
            'before:bg-base-black': headerMode === 'dark' && !isActive,
            'before:bg-primary-orange': headerMode === 'orange' && !isActive,
            'before:bg-base-silver': headerMode === 'silver' && !isActive,
            'shadow-[0px_3px_4px_-2px_rgba(0,0,0,0.15)] before:opacity-100':
                scrollDirection === 'up' && currentScrollY > 0 && !isMenuOpen,
            'shadow-[0px_3px_4px_-2px_rgba(0,0,0,0.15)]':
                scrollDirection === 'down' && currentScrollY > 0,
            'shadow-[0px_3px_4px_-2px_rgba(0,0,0,0)] before:opacity-0':
                scrollDirection === 'up' || currentScrollY === 0 || isMenuOpen,
        }"
    >
        <div
            v-show="isActive"
            ref="bgRef"
            class="pointer-events-none absolute left-0 top-0 size-full bg-primary-orange"
        ></div>
        <div
            class="relative z-[10] transition-transform duration-300 ease-in-out-quad"
            :class="{
                'translate-y-[1vw] xs:translate-y-[4px] sm:translate-y-[1.5625vw] lg:translate-y-[1.06vw] 3xl:translate-y-[20.3px]':
                    scrollDirection === 'down' ||
                    currentScrollY === 0 ||
                    isActive,
                'translate-y-0': scrollDirection === 'up' && currentScrollY > 0,
            }"
        >
            <div
                class="relative h-auto px-[4.07vw] xs:px-[16px] sm:px-[5.21vw] lg:overflow-visible lg:px-[2.65vw] 3xl:px-[50.8px]"
            >
                <!-- static menu -->
                <nav
                    class="flex w-full items-center justify-between py-[3.05vw] xs:pt-[12px] sm:py-[1.5625vw] lg:py-[1.06vw] 3xl:py-[20.3px]"
                >
                    <div
                        v-show="isActive"
                        ref="menuOverlay"
                        class="pointer-events-none absolute left-0 top-0 z-10 h-[80px] w-full"
                        :style="{
                            backgroundImage:
                                'linear-gradient(to bottom, #ff6400 55%, transparent 100%)',
                        }"
                    ></div>
                    <NuxtLink
                        :to="$localePath('/')"
                        class="z-20 flex cursor-pointer items-center gap-[2.03vw] xs:gap-[8px] sm:gap-[1.04vw] lg:gap-[8px]"
                        @click="isMenuOpen && toggleMenu"
                        data-trigger="header"
                        :aria-label="$t('ariaLabel.header.home')"
                        v-sendGAsignal="{
                            event_category: 'header',
                            event_label: '首頁',
                        }"
                    >
                        <div
                            class="h-[8.14vw] w-[8.14vw] xs:h-[32px] xs:w-[32px] sm:h-[4.17vw] sm:w-[4.17vw] lg:h-[2.12vw] lg:w-[2.12vw] 3xl:h-[40.6px] 3xl:w-[40.6px]"
                        >
                            <SvgStaticGamaGlobe
                                class="h-full w-full"
                                :isDark="isDark && !isMenuOpen"
                            />
                        </div>
                        <div
                            class="h-[5.09vw] w-[18.83vw] xs:h-[20px] xs:w-[74px] sm:h-[2.6vw] sm:w-[9.64vw] lg:h-[1.32vw] lg:w-[5vw] 3xl:h-[25.4px] 3xl:w-[95.2px]"
                        >
                            <SvgGamaniaText
                                class="h-full w-full"
                                :isDark="isDark && !isMenuOpen"
                            />
                        </div>
                    </NuxtLink>
                    <div
                        v-if="isDesktop"
                        class="slt-transition-color z-20 hidden gap-[0.61vw] lg:flex 3xl:gap-[39.1px]"
                        :class="{ 'text-base-white': isDark }"
                    >
                        <template v-for="item in proccessedMenuLinks?.slice(1)">
                            <NuxtLink
                                class="btn group flex items-center justify-center gap-[2px] rounded-full px-4 py-1 duration-300"
                                :to="
                                    item.type === 'linkInternal'
                                        ? $localePath(item.href)
                                        : '#'
                                "
                                :target="
                                    item.type === 'linkExternal'
                                        ? '_blank'
                                        : '_self'
                                "
                                @click.prevent="
                                    item.type === 'linkExternal'
                                        ? handleExternalLinkClick(item)
                                        : null
                                "
                                data-trigger="header"
                                v-sendGAsignal="{
                                    event_category: 'header',
                                    event_label: item?.name,
                                }"
                            >
                                <p
                                    class="slt-body-3 relative overflow-hidden whitespace-pre"
                                >
                                    <span>
                                        <span
                                            v-for="(text, index) in item.name"
                                            :key="text + index"
                                            :style="{
                                                transitionDelay: `${
                                                    (index * 0.16) /
                                                    item.name.length
                                                }s`,
                                            }"
                                            class="inline-block transition-transform duration-300 ease-in-out-cubic group-hover:translate-y-[-100%]"
                                            >{{ text }}</span
                                        >
                                    </span>
                                    <span class="absolute left-0 top-[100%]">
                                        <span
                                            v-for="(text, index) in item.name"
                                            :key="text + index"
                                            :style="{
                                                transitionDelay: `${
                                                    (index * 0.16) /
                                                    item.name.length
                                                }s`,
                                            }"
                                            class="inline-block transition-transform duration-300 ease-in-out-cubic group-hover:translate-y-[-100%]"
                                            >{{ text }}</span
                                        >
                                    </span>
                                </p>
                                <div
                                    v-if="item.type === 'linkExternal'"
                                    class="relative inline-block h-[1.06vw] w-[1.06vw] overflow-hidden align-middle 3xl:h-[20.3px] 3xl:w-[20.2px]"
                                >
                                    <SvgArrowOblique16
                                        :isDark="isDark"
                                        class="btn__arrow btn__arrow__external--main size-full"
                                        :stroke-width="1.6"
                                    />
                                    <SvgArrowOblique16
                                        :isDark="isDark"
                                        class="btn__arrow btn__arrow__external--hover size-full"
                                        :stroke-width="1.67"
                                    />
                                </div>
                            </NuxtLink>
                        </template>
                    </div>
                    <div
                        class="z-20 flex items-center gap-[4.07vw] xs:gap-[16px] sm:gap-[2.08vw] lg:gap-[1.06vw] 3xl:gap-[20.3px]"
                    >
                        <!-- Mobile Language Select -->
                        <div
                            v-if="!isDesktop"
                            v-show="isMenuOpen"
                            class="h-[6.1vw] w-[6.1vw] xs:h-[24px] xs:w-[24px] sm:h-[3.125vw] sm:w-[3.125vw] lg:hidden"
                            v-sendGAsignal="{
                                event_category: 'header',
                                event_label: '切換語言',
                            }"
                        >
                            <InputSelect
                                :config="languageListConfig"
                                :type="'language'"
                                :variant="
                                    isDark && !isMenuOpen ? 'dark' : 'light'
                                "
                                @change="switchLanguageMobile"
                            />
                        </div>
                        <!-- Desktop Language Select -->
                        <div
                            v-if="isDesktop"
                            class="relative hidden cursor-pointer lg:block lg:h-[1.59vw] lg:w-[1.59vw] 3xl:h-[30.5px] 3xl:w-[30.5px]"
                            @click="languageListOpen = !languageListOpen"
                            v-sendGAsignal="{
                                event_category: 'header',
                                event_label: '切換語言',
                            }"
                        >
                            <div
                                class="absolute left-[50%] top-[50%] h-[2.12vw] w-[2.12vw] -translate-x-1/2 -translate-y-1/2 rounded-[8px] 3xl:h-[40.6px] 3xl:w-[40.6px]"
                                :class="{
                                    'bg-primary-orange':
                                        languageListOpen && isDark,
                                    'bg-base-Metal':
                                        languageListOpen && !isDark,
                                }"
                            ></div>
                            <SvgLanguage24
                                class="relative h-full w-full"
                                :color="
                                    languageListOpen && isDesktop
                                        ? isDark
                                            ? '#353535'
                                            : '#d3d9e2'
                                        : isDark
                                          ? '#ff6400'
                                          : 'black'
                                "
                            />
                            <!-- Language dropdown -->
                            <div
                                class="absolute left-[50%] top-[calc(100%+0.79vw)] hidden -translate-x-1/2 overflow-hidden rounded-[8px] lg:block 3xl:top-[calc(100%+15.2px)]"
                                :class="{
                                    'pointer-events-none': !languageListOpen,
                                    'pointer-events-auto': languageListOpen,
                                }"
                            >
                                <button
                                    v-for="(loc, index) in locales"
                                    :key="loc.code"
                                    :style="{
                                        transform: `translateY(${languageListOpen ? '0' : `${-100 * (index + 1.02)}%`})`,
                                    }"
                                    class="realtive slt-body-3-d flex h-[1.98vw] w-[4.89vw] cursor-pointer select-none items-center justify-center transition-transform duration-500 ease-in-out-quint 3xl:h-[38.1px] 3xl:w-[94px]"
                                    :class="{
                                        'rounded-t-[7px]': index === 0,
                                        'rounded-b-[7px]':
                                            index === locales.length - 1,
                                        'bg-primary-orange text-base-black hover:bg-base-dimGrey hover:text-primary-orange':
                                            isDark && locale !== loc.code,
                                        'bg-base-Metal text-base-white hover:bg-base-blueGrey hover:text-base-Metal':
                                            !isDark && locale !== loc.code,
                                        'bg-base-dimGrey text-primary-orange':
                                            locale === loc.code && isDark,
                                        'bg-base-blueGrey text-base-Metal':
                                            locale === loc.code && !isDark,
                                    }"
                                    @click="switchLanguageDesktop(loc)"
                                    v-sendGAsignal="{
                                        event_category: 'header',
                                        event_label: `切換語言: ${loc.value}`,
                                    }"
                                >
                                    {{ loc.value }}
                                </button>
                            </div>
                        </div>
                        <div
                            class="mr-[2.03vw] h-[6.1vw] w-[6.1vw] cursor-pointer xs:mr-[8px] xs:h-[24px] xs:w-[24px] sm:mr-[1.04vw] sm:h-[3.125vw] sm:w-[3.125vw] lg:mr-0 lg:h-[1.59vw] lg:w-[1.59vw] 3xl:h-[30.5px] 3xl:w-[30.5px]"
                            @click="setIsSearchOpen(true)"
                            v-sendGAsignal="{
                                event_category: 'header',
                                event_label: '搜尋',
                            }"
                        >
                            <SvgSearch24
                                class="h-full w-full"
                                :isDark="isDark && !isMenuOpen"
                            />
                        </div>
                        <div
                            v-if="!isDesktop"
                            class="cursor-pointer overflow-hidden rounded-[24.43vw] transition-transform hover:scale-105 focus:scale-95 xs:rounded-[96px] sm:rounded-[12.5vw] lg:hidden"
                            :class="{
                                'bg-base-bgGrey':
                                    ((route.name ===
                                        $localeRoute('about', locale).name ||
                                        route.name ===
                                            $localeRoute('news', locale).name ||
                                        route.name ===
                                            $localeRoute('awards', locale)
                                                .name ||
                                        route.name ===
                                            $localeRoute('location', locale)
                                                .name) &&
                                        !isDark &&
                                        currentScrollY === 0 &&
                                        !isActive) ||
                                    headerMode === 'silver',
                                'bg-base-dimGrey': isDark && !isActive,
                                'bg-base-silver':
                                    ((route.name !==
                                        $localeRoute('about', locale).name &&
                                        route.name !==
                                            $localeRoute('news', locale).name &&
                                        route.name !==
                                            $localeRoute('awards', locale)
                                                .name &&
                                        route.name !==
                                            $localeRoute('location', locale)
                                                .name) ||
                                        currentScrollY > 0) &&
                                    !isDark &&
                                    !isActive &&
                                    headerMode !== 'silver',
                                'bg-base-black': isActive,
                                'animate-buttonPress': isPressed,
                            }"
                        >
                            <button
                                @click="pressClose"
                                class="flex h-[10.18vw] w-[15.27vw] items-center justify-center xs:h-[40px] xs:w-[60px] sm:h-[5.2vw] sm:max-h-[40px] sm:w-[7.8vw] sm:max-w-[60px]"
                                v-sendGAsignal="{
                                    event_category: 'header',
                                    event_label: isMenuOpen
                                        ? '關閉 mega menu'
                                        : '開啟 mega menu',
                                }"
                            >
                                <div v-if="!isMenuOpen" class="flex gap-[3px]">
                                    <span class="button-bar"></span>
                                    <span class="button-bar"></span>
                                    <span class="button-bar"></span>
                                </div>
                                <SvgClose24
                                    v-else
                                    class="h-6 w-6"
                                    :color="isMenuOpen ? '#ff6400' : 'black'"
                                />
                            </button>
                        </div>
                    </div>
                </nav>
                <!-- responsive menu -->
                <nav
                    v-if="!isDesktop"
                    v-show="isActive"
                    ref="responsiveMenuRef"
                    class="mb-2 flex h-[calc(var(--window-height)-var(--header-height))] overflow-y-hidden px-4 pb-[55px] pt-8 sm:pt-10"
                >
                    <!-- menu lists -->
                    <div class="slt-scrollbar-hide w-full overflow-y-scroll">
                        <ul class="flex flex-col gap-4 sm:gap-6">
                            <li
                                v-for="(item, index) in menuLinks"
                                :key="item.name"
                                class="slt-menu w-[90%] cursor-pointer overflow-hidden sm:w-[95%]"
                            >
                                <div
                                    class="btn flex w-full items-center transition-transform duration-700 ease-in-out-quint"
                                    :class="{
                                        'translate-y-full': !isMenuTextActive,
                                        'translate-y-0': isMenuTextActive,
                                    }"
                                    :style="{
                                        transitionDelay: `${index * 0.05 + 0.2}s`,
                                    }"
                                >
                                    <NuxtLink
                                        v-if="item.type !== 'linkExternal'"
                                        :class="{
                                            'text-white': isCurrentRoute(
                                                item.href,
                                            ),
                                        }"
                                        :to="$localePath(item.href)"
                                        @click.prevent="toggleMenu"
                                        data-trigger="header"
                                        v-sendGAsignal="{
                                            event_category: 'header',
                                            event_label: item?.name,
                                        }"
                                    >
                                        <p
                                            class="relative overflow-hidden text-left"
                                            v-html="htmlPurify(item.name)"
                                        ></p>
                                    </NuxtLink>
                                    <button
                                        v-else
                                        @click.prevent="
                                            (toggleMenu(),
                                            handleExternalLinkClick(item))
                                        "
                                        data-trigger="header"
                                        v-sendGAsignal="{
                                            event_category: 'header',
                                            event_label: item?.name,
                                        }"
                                    >
                                        <p
                                            class="relative overflow-hidden text-left"
                                            v-html="htmlPurify(item.name)"
                                        ></p>
                                    </button>
                                    <div
                                        class="btn__arrow__group"
                                        v-if="item.type === 'linkExternal'"
                                    >
                                        <SvgArrowOblique16
                                            class="btn__arrow btn__arrow__external--main"
                                            :strokeWidth="strokeWidth"
                                        />
                                        <SvgArrowOblique16
                                            class="btn__arrow btn__arrow__external--hover"
                                            :strokeWidth="strokeWidth"
                                        />
                                    </div>
                                </div>
                            </li>
                        </ul>
                        <!-- sns -->
                        <div class="mt-5 overflow-hidden sm:mt-6">
                            <div
                                class="flex gap-2 pb-[30px] transition-transform duration-700 ease-in-out-quint sm:gap-3 sm:pb-0"
                                :class="{
                                    'translate-y-full': !isMenuTextActive,
                                    'translate-y-0': isMenuTextActive,
                                }"
                                :style="{
                                    transitionDelay: `${menuLinks?.length * 0.05 + 0.2}s`,
                                }"
                            >
                                <NuxtLink
                                    v-for="(item, index) in snsData"
                                    :key="item.name"
                                    :to="item.href"
                                    external
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="flex size-[12.21vw] items-center justify-center rounded-full bg-base-silver xs:size-12"
                                    v-sendGAsignal="{
                                        event_category: 'header',
                                        event_label: item.name,
                                    }"
                                >
                                    <component
                                        :is="getSnsIcon(item.name)"
                                        class="size-[6.1vw] xs:size-6"
                                    />
                                </NuxtLink>
                            </div>
                        </div>
                    </div>
                    <div
                        :style="{
                            transitionDelay: '0.6s',
                        }"
                        class="absolute bottom-[55px] left-0 h-[30px] w-full bg-[linear-gradient(to_bottom,rgba(255,100,0,0)_0%,rgba(255,100,0,0.5)_50%,rgba(255,100,0,1)_100%)] transition-opacity ease-linear sm:hidden"
                        :class="{
                            'opacity-0': !isMenuTextActive,
                            'opacity-100': isMenuTextActive,
                        }"
                    ></div>
                </nav>
            </div>
        </div>
        <!-- gama globe -->
        <div
            v-if="!isDesktop"
            v-show="isActive"
            ref="gamaGlobeRef"
            class="pointer-events-none absolute left-0 top-0 z-[15] size-full overflow-hidden pl-[335px] sm:pl-[750px]"
        >
            <SvgGamaglobe1
                class="gama-globe bottom-[170px] right-[-32px] sm:bottom-[600px] sm:right-[-38px]"
            />
            <SvgGamaglobe2
                class="gama-globe bottom-[250px] right-[20px] hidden sm:block"
            />
            <SvgGamaglobe3
                class="gama-globe bottom-[50px] right-[125px] hidden sm:block"
            />
            <SvgGamaglobe4
                class="gama-globe bottom-[130px] right-[310px] hidden sm:block"
            />
            <SvgGamaglobe5
                class="gama-globe bottom-[-80px] left-[-30px] sm:bottom-[-40px] sm:left-[70px]"
            />
        </div>
    </header>
</template>

<style scoped>
.egg .slt-transition-color {
    transition: color 0.5s linear;
}

.button-bar {
    @apply h-[8px] w-[4px] rounded-[9px] bg-primary-orange;
}

.gama-globe {
    @apply absolute size-[35.62vw] xs:size-[140px] sm:size-[154px];
}

.sns-hover {
    @apply duration-100 ease-in-out-cubic sm:hover:scale-110;
}

/* button arrow animation */
.btn__arrow__group {
    @apply relative ml-1 inline-block size-[10.18vw] overflow-hidden align-middle xs:size-[40px] sm:size-[64px];
}
.btn__arrow {
    @apply absolute right-0 top-0 size-full;
    transition: transform 0.4s cubic-bezier(0.645, 0.045, 0.355, 1);
}
.btn__arrow--hover {
    transform: translate3d(-100%, 0, 0);
}
.btn__arrow__external--hover {
    transform: translate3d(-100%, 100%, 0);
}

@media (min-width: 640px) {
    .btn:hover .btn__arrow--main {
        transform: translate3d(100%, 0, 0);
    }
    .btn:hover .btn__arrow--hover {
        transform: translate3d(0, 0, 0);
    }
    .btn:hover .btn__arrow__external--main {
        transform: translate3d(100%, -100%, 0);
    }
    .btn:hover .btn__arrow__external--hover {
        transform: translate3d(0, 0, 0);
    }
}
</style>
